import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { getReport, ReportsRequest, downloadReport, getReportById } from "src/network/graphql/analyticsService";
import { immediateToast } from "izitoast-react";

export type Reports = {
  report_id: string;
  report_type: string;
  status: string;
  report_date: string;
  report_name: string;
};

const initialReports: Reports[] = [];

const reportsSlice = createSlice({
  name: "reports",
  initialState: {
    loading: false,
    downloading: false,
    syncing: false,
    error: null,
    reports: {
      list: initialReports,
      size: 0
    }
  },
  reducers: {
    fetchReportsStart: (state) => {
      state.loading = true;
    },
    fetchReportsSuccess: (state, action) => {
      state.loading = false;
      state.reports = action.payload;
    },
    fetchReportsFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSingleReport: (state, action) => {
      const updatedReport = action.payload;
      const reportIndex = state.reports.list.findIndex((report) => report.report_id === updatedReport.report_id);

      if (reportIndex !== -1) {
        state.reports.list[reportIndex] = updatedReport;
      }
    },
    downloadReportStart: (state) => {
      state.downloading = true;
    },
    downloadReportSuccess: (state) => {
      state.downloading = false;
    },
    downloadReportFail: (state, action) => {
      state.downloading = false;
      state.error = action.payload;
    },
    syncingReportStart: (state) => {
      state.syncing = true;
    },
    syncingReportSuccess: (state) => {
      state.syncing = false;
    },
    syncingReportFail: (state, action) => {
      state.syncing = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchReportsStart,
  fetchReportsSuccess,
  fetchReportsFail,
  updateSingleReport,
  downloadReportStart,
  downloadReportSuccess,
  downloadReportFail,
  syncingReportStart,
  syncingReportSuccess,
  syncingReportFail
} = reportsSlice.actions;

export const updateSingleReportAction = (reportId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(syncingReportStart());
    try {
      const response = await getReport({
        size: 1,
        from: 0,
        filter: {
          report_filter: {
            report_ids: [reportId]
          }
        }
      });

      if (response.data.search_report.reports.length > 0) {
        const updatedReport = response.data.search_report.reports[0];

        dispatch(updateSingleReport(updatedReport));
        immediateToast("success", {
          message: "Report updated successfully",
          timeout: 3000,
          position: "topCenter"
        });
        dispatch(syncingReportSuccess());
      }
    } catch (error) {
      dispatch(syncingReportFail(error));
    }
  };
};

export const downloadReportAction = (report: Reports) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadReportStart());
    try {
      const response = await downloadReport({ report_id: report.report_id });

      const downloadUrl = response.data.get_report_download_url.download_url;

      const link = document.createElement("a");

      link.href = downloadUrl;

      if (report.report_name?.trim()) {
        link.download = report.report_name;
      }

      link.click();

      immediateToast("success", {
        message: "Report downloaded successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(downloadReportSuccess());
    } catch (error) {
      console.error("Error downloading report:", error);
      dispatch(downloadReportFail(error));
    }
  };
};

export const fetchReportsAction = (data: ReportsRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchReportsStart());
    try {
      const response = await getReport(data);

      dispatch(
        fetchReportsSuccess({
          list: response.data.search_report.reports,
          size: response.data.search_report.total_size
        })
      );
    } catch (error) {
      dispatch(fetchReportsFail(error));
    }
  };
};

export const fetchReportDataAction = (reportId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchReportsStart());
    try {
      const response = await getReportById(reportId);
      const report = response.data.get_report;
      const structuredResponse = {
        report_id: report.report_id,
        report_name: report.report_name,
        report_date: report.report_date,
        report_type: report.report_type,
        status: report.status
      };

      dispatch(
        fetchReportsSuccess({
          list: [structuredResponse],
          size: 1
        })
      );
    } catch (error) {
      dispatch(fetchReportsFail(error));
    }
  };
};

export default reportsSlice.reducer;
